import { ContentType, SourceType, Collection, WebsiteResponse, SnippetOnlyDomains, UrlParser } from "@authory-packages/core";
import { useEffect, useState } from "react";
import { AddSourceUnsupported } from "./AddSourceUnsupported";
import { AddSourceCompleted } from "./AddSourceCompleted";
import { AliasesFormik } from "../../EditAliases";
import { AddSourceURL } from "./AddSourceURL";
import { AddSourceEditOptions } from "./AddSourceEditOptions";
import {
    AddSourceFormik,
    useGetAddSourceFormik,
    parseSourceUrl,
    findExistingSource,
    SourceExistsFormTargets,
    getSourceExistsDescription,
    CompleteTarget,
    getSourceCompleteTitle,
    getSourceCompleteDescription,
    getSourceExistsTextAreaPlaceholder
} from "./AddSource.utils";
import { clone } from "lodash";
import { ManualImportPathTaken, UniversalAddTrackingEventNames } from "../UniversalAddContentTracking";
import { AddSourceContentNotFound } from "./AddSourceContentNotFound";
import { AddSourceAlreadyExists } from "./AddSourceAlreadyExists";
import { AddSourceFindArticles } from "./AddSourceFindArticles";
import { AddSourceExistsForm } from "./AddSourceExistsForm";
import { getSlug } from "../../../utils/getSlug";

export enum AddSourceWizardStage {
    AddURL = "AddURL",
    EditOptions = "EditOptions",
    Unsupported = "Unsupported",
    ContentNotFound = "ContentNotFound",
    Completed = "Completed",
    AlreadyExists = "AlreadyExists",
    ExistsForm = "ExistsForm",
    CantFindArticles = "CantFindArticles"
}

interface AddSourceProps {
    sources: WebsiteResponse[];
    sourceType: SourceType;
    names: string[];
    collection?: Collection;
    onCloseHandler: () => void;
    onAutommatedImportSubmit: (data: AddSourceFormik) => Promise<void>;
    onSourceExists: (slug: string, notes?: string) => Promise<void>;
    onStageChange: (stage: AddSourceWizardStage) => void;
    trackEvent: (eventName: string, data: any) => void;
    onAliasesSubmit: (data: AliasesFormik) => Promise<void>;
    onGoToAddManual: (pathTaken: ManualImportPathTaken, isSnippetImport: boolean, targetContentType: ContentType, url?: string) => void;
    onSourceTypeChange: (type: SourceType) => void;
    onBackHandler: () => void;
    hideBackButton: boolean;
    startingStage?: AddSourceWizardStage;
}

export const AddSource = ({
    onCloseHandler,
    sources,
    sourceType,
    names,
    collection,
    hideBackButton,
    startingStage = AddSourceWizardStage.AddURL,
    onAliasesSubmit,
    onBackHandler,
    onAutommatedImportSubmit,
    onSourceExists,
    trackEvent,
    onGoToAddManual,
    onStageChange,
    onSourceTypeChange
}: AddSourceProps) => {
    const [stage, setStage] = useState(startingStage);
    const [stageHistory, setStageHistory] = useState<AddSourceWizardStage[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [addManualTarget, setAddManualTarget] = useState(ContentType.Article);
    const [completeTarget, setCompleteTarget] = useState(CompleteTarget.Regular);
    const [sourceExistsFormTarget, setSourceExistsFormTarget] = useState<SourceExistsFormTargets | undefined>();

    const goToStage = (targetStage: AddSourceWizardStage) => {
        onStageChange(targetStage);
        setStageHistory(stageHistory.concat(stage));
        setStage(targetStage);
    };

    const goBack = () => {
        const prevStage = stageHistory.slice(-1)[0];
        setStage(prevStage);
        onStageChange(prevStage);
        setStageHistory(stageHistory.slice(0, stageHistory.length - 1));
    };

    const formik = useGetAddSourceFormik(
        sourceType,
        async (data) => {
            setSubmitting(true);

            try {
                const clonedData = clone(data);
                clonedData.url = parseSourceUrl(clonedData.url);
                trackEvent(UniversalAddTrackingEventNames.CreateSource, { ...clonedData });

                // save aliases on web flow AND submit domain only
                if (data.type === SourceType.Web) {
                    await onAliasesSubmit({
                        aliases: data.aliases.filter((nm) => !!nm.length)
                    });

                    const domain = UrlParser.extractDomain(clonedData.url);
                    if (domain) clonedData.url = domain;
                }

                await onAutommatedImportSubmit(clonedData);

                // Exception: If on collection context, system will jump to the auto-update screen flow on parent component and not complete yet
                goToStage(AddSourceWizardStage.Completed);
            } catch {}

            setSubmitting(false);
        },
        names.length > 1 ? names.slice(1) : []
    );

    useEffect(() => {
        onSourceTypeChange(formik.values.type);
    }, [formik.values.type]);

    return (
        <>
            {stage === AddSourceWizardStage.AddURL && (
                <AddSourceURL
                    hideBackButton={hideBackButton}
                    formik={formik}
                    onBackHandler={onBackHandler}
                    userName={names[0]}
                    onGoToAddManual={() => {
                        if (sourceType === SourceType.Youtube) {
                            onGoToAddManual(ManualImportPathTaken.YoutubeOther, true, ContentType.Video);
                        } else if (sourceType === SourceType.PodcastRssFeed) {
                            onGoToAddManual(ManualImportPathTaken.PodcastEpisode, true, ContentType.PodcastItem);
                        } else {
                            onGoToAddManual(ManualImportPathTaken.Articles, true, ContentType.Article);
                        }
                    }}
                    onSourceUrlHandler={async () => {
                        // First thing, kick validation and check for url (other fields are in a different screen)
                        const validation = await formik.validateForm();
                        if ("url" in validation) {
                            formik.submitForm();
                            return;
                        }

                        formik.setTouched({});

                        // 2nd check, snippet only sources
                        try {
                            const domain = UrlParser.extractDomain(formik.values.url);

                            if (domain && domain in SnippetOnlyDomains) {
                                setAddManualTarget(SnippetOnlyDomains[domain]);
                                goToStage(AddSourceWizardStage.Unsupported);
                                return;
                            }
                        } catch {
                            return;
                        }

                        // 3rd check, source exists
                        try {
                            const targetSrc = findExistingSource(formik.values.url, sources);

                            if (targetSrc) {
                                if (targetSrc.manualImportFlowTag) {
                                    await onSourceExists(targetSrc?.canonicalSlug);
                                    goToStage(AddSourceWizardStage.Completed);
                                } else {
                                    goToStage(AddSourceWizardStage.AlreadyExists);
                                }
                                return;
                            }
                        } catch {
                            return;
                        }

                        // 4th check, source is not web source
                        if (formik.values.type !== SourceType.Web) {
                            goToStage(AddSourceWizardStage.EditOptions);
                            return;
                        }

                        if (formik.values.type === SourceType.Web) {
                            try {
                                const domain = UrlParser.extractDomain(formik.values.url);

                                if (domain) {
                                    formik.setFieldValue("name", domain);
                                }
                            } catch {}
                        }

                        // we got results, let user proceed
                        goToStage(AddSourceWizardStage.EditOptions);
                        // }

                        // let domain = "";

                        // try {
                        //     domain = UrlParser.extractDomain(formik.values.url) || "";
                        // } catch {}

                        try {
                            // trackEvent(UniversalAddTrackingEventNames.FindUserComplete, {
                            //     url: formik.values.url,
                            //     status: numResults === 0 ? "error" : "success",
                            //     domain
                            // });
                        } catch {}
                    }}
                />
            )}
            {stage === AddSourceWizardStage.ContentNotFound && (
                <AddSourceContentNotFound
                    userName={names[0]}
                    url={formik.values.url}
                    onBackHandler={goBack}
                    aliases={formik.values.aliases.filter((nm) => !!nm.length)}
                    goToAddManual={(pathTaken, isSnippetImport, targetContentType) => {
                        onGoToAddManual(pathTaken, isSnippetImport, targetContentType);
                    }}
                    onImportEverything={() => {
                        formik.setFieldValue("importEveryting", true);
                        goToStage(AddSourceWizardStage.EditOptions);
                    }}
                    onSomethingElse={() => {
                        formik.setFieldValue("somethingElse", true);
                        goToStage(AddSourceWizardStage.EditOptions);
                    }}
                />
            )}
            {stage === AddSourceWizardStage.EditOptions && (
                <AddSourceEditOptions
                    formik={formik}
                    loading={submitting}
                    onBackHandler={() => {
                        formik.setFieldValue("importEveryting", false);
                        formik.setFieldValue("somethingElse", false);
                        goBack();
                    }}
                />
            )}
            {stage === AddSourceWizardStage.AlreadyExists && (
                <AddSourceAlreadyExists
                    computedSourceSlug={getSlug(formik.values.url)}
                    onBackHandler={goBack}
                    onCantFindArticles={() => {
                        goToStage(AddSourceWizardStage.CantFindArticles);
                    }}
                    onGoToSourceExistsForm={(target) => {
                        setSourceExistsFormTarget(target);
                        goToStage(AddSourceWizardStage.ExistsForm);
                    }}
                />
            )}
            {stage === AddSourceWizardStage.ExistsForm && sourceExistsFormTarget && (
                <AddSourceExistsForm
                    onBackHandler={goBack}
                    placeholder={getSourceExistsTextAreaPlaceholder(sourceExistsFormTarget)}
                    description={getSourceExistsDescription(sourceExistsFormTarget)}
                    onSourceExistsSubmit={async (data) => {
                        const targetSrc = findExistingSource(formik.values.url, sources);

                        if (!targetSrc) return;

                        const finalNotes = `
Reason: ${sourceExistsFormTarget}
User Reply:
${data.notes}
`;

                        await onSourceExists(targetSrc.canonicalSlug, finalNotes);

                        setCompleteTarget(CompleteTarget.Troubleshoot);
                        goToStage(AddSourceWizardStage.Completed);
                    }}
                />
            )}
            {stage === AddSourceWizardStage.CantFindArticles && (
                <AddSourceFindArticles
                    onBackHandler={goBack}
                    computedSourceSlug={getSlug(formik.values.url)}
                    sourceCanonicalSlug={findExistingSource(formik.values.url, sources)?.canonicalSlug || ""}
                />
            )}
            {stage === AddSourceWizardStage.Unsupported && (
                <AddSourceUnsupported
                    url={formik.values.url}
                    onGoBackHandler={goBack}
                    onGoToAddManual={() => {
                        onGoToAddManual(ManualImportPathTaken.AddSourceRejected, true, addManualTarget, formik.values.url);
                    }}
                />
            )}
            {stage === AddSourceWizardStage.Completed && (
                <AddSourceCompleted
                    onCloseHandler={onCloseHandler}
                    title={getSourceCompleteTitle(completeTarget)}
                    description={getSourceCompleteDescription(completeTarget, collection?.label)}
                    showAddMoreContent={completeTarget === CompleteTarget.Regular}
                />
            )}
        </>
    );
};
